import { CheckCircle2, ChevronRight, Inbox } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";

import { AGE_AMBER_MINUTES, AGE_RED_MINUTES, HISTORY_PAGE_SIZE } from "../constants/payoutRescue.constants";
import { PayoutStatus } from "../services/payoutRescue.service";
import type { RescuePayout, RescueTab } from "../types/payoutRescue.types";
import {
  ageMinutes,
  destinationBeneficiary,
  formatAge,
  formatMoney,
  shortId,
} from "../utils/payoutRescueFormat";
import { RescueStatusPill } from "./RescueStatusPill";

export interface RescueTableProps {
  tab: RescueTab;
  rows: RescuePayout[];
  orgName: (organizationId: string) => string | null;
  isLoading: boolean;
  onRowClick: (payout: RescuePayout) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** History pagination (server-side). Ignored on the queue tabs, which render the full ≤200-row page. */
  historyPage: number;
  historyTotal: number;
  onHistoryPageChange: (page: number) => void;
}

/** Age SLA color: >4h red, >1h amber (the queue is sorted oldest first — age drives the escalation). */
function ageToneClass(minutes: number): string {
  if (minutes > AGE_RED_MINUTES) return "text-destructive";
  if (minutes > AGE_AMBER_MINUTES) return "text-warning";
  return "text-foreground";
}
function ageDotClass(minutes: number): string {
  if (minutes > AGE_RED_MINUTES) return "bg-destructive";
  if (minutes > AGE_AMBER_MINUTES) return "bg-warning";
  return "bg-success";
}

/**
 * Dense ops table of the rescue queue. Never renders provider identity; the beneficiary column appears only
 * when at least one row carries the destination snapshot (a future backend slice — see the module types). The
 * empty state of the pending tab is the NORM (auto-dispatch handles the happy path) — calm, not sad.
 */
export function RescueTable({
  tab,
  rows,
  orgName,
  isLoading,
  onRowClick,
  hasActiveFilters,
  onClearFilters,
  historyPage,
  historyTotal,
  onHistoryPageChange,
}: RescueTableProps) {
  const { t, activeLanguage } = useTranslations("payoutRescue");

  const hasBeneficiaryData = rows.some((row) => destinationBeneficiary(row.destination) !== null);

  const columns = useMemo<ColocaDataTableColumn<RescuePayout>[]>(() => {
    const now = new Date();
    const cols: ColocaDataTableColumn<RescuePayout>[] = [
      {
        id: "age",
        header: t("table_th_age"),
        cell: (row) => {
          const minutes = ageMinutes(row.created_at, now);
          return (
            <span className="inline-flex items-center gap-2" title={formatDateTime(row.created_at)}>
              <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", ageDotClass(minutes))} />
              <span
                className={cn(
                  "whitespace-nowrap font-mono text-sm font-medium tabular-nums",
                  ageToneClass(minutes),
                )}
              >
                {formatAge(minutes)}
              </span>
            </span>
          );
        },
      },
      {
        id: "org",
        header: t("table_th_org"),
        cell: (row) => {
          const name = orgName(row.organization_id);
          return name ? (
            <span className="text-sm font-medium text-foreground">{name}</span>
          ) : (
            // Catalog miss (still loading / no organizations:read) — the uuid tail still identifies the org.
            <span className="font-mono text-xs text-muted-foreground" title={row.organization_id}>
              {shortId(row.organization_id)}
            </span>
          );
        },
      },
      {
        id: "corridor",
        header: t("table_th_corridor"),
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5">
            <ColocaBadge variant="default" className="font-mono text-[10px]">
              {row.country}
            </ColocaBadge>
            <ColocaBadge variant="secondary" className="font-mono text-[10px]">
              {formatRail(row.rail)}
            </ColocaBadge>
            <span className="text-xs font-medium text-muted-foreground">{row.currency}</span>
          </span>
        ),
      },
    ];
    if (hasBeneficiaryData) {
      cols.push({
        id: "beneficiary",
        header: t("table_th_beneficiary"),
        cell: (row) => {
          const name = destinationBeneficiary(row.destination);
          return name ? (
            <span className="text-sm text-foreground">{name}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      });
    }
    cols.push(
      {
        id: "concept",
        header: t("table_th_concept"),
        // `block` is load-bearing: `max-width` does not apply to an inline box, so without it the concept
        // has no cap and stretches the table.
        cell: (row) =>
          row.concept ? (
            <span className="block max-w-44 truncate text-sm text-muted-foreground" title={row.concept}>
              {row.concept}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: "net",
        header: t("table_th_net"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <span className="whitespace-nowrap font-mono text-sm font-semibold tabular-nums">
            {formatMoney(row.net_amount, activeLanguage)} {row.currency}
          </span>
        ),
      },
      {
        id: "status",
        header: t("table_th_status"),
        cell: (row) => <RescueStatusPill status={row.status} size="sm" />,
      },
      {
        id: "action",
        header: "",
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
            {row.status === PayoutStatus.CREATED ? t("action_review") : t("action_view")}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        ),
      },
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, activeLanguage, orgName, hasBeneficiaryData]);

  const emptyTitleKey =
    tab === "pending"
      ? "empty_pending_title"
      : tab === "processing"
        ? "empty_processing_title"
        : "empty_history_title";
  const emptyDescriptionKey =
    tab === "pending"
      ? "empty_pending_description"
      : tab === "processing"
        ? "empty_processing_description"
        : "empty_history_description";

  const paginationFooter =
    tab === "history" && historyTotal > HISTORY_PAGE_SIZE ? (
      <ColocaPagination
        page={historyPage}
        perPage={HISTORY_PAGE_SIZE}
        total={historyTotal}
        withSeparator={false}
        onPageChange={onHistoryPageChange}
        summary={t("pagination_summary", {
          from: (historyPage - 1) * HISTORY_PAGE_SIZE + 1,
          to: Math.min(historyPage * HISTORY_PAGE_SIZE, historyTotal),
          total: historyTotal,
        })}
      />
    ) : tab === "pending" && rows.length > 0 ? (
      <p className="px-4 py-2 text-xs text-muted-foreground">{t("footer_order_pending")}</p>
    ) : undefined;

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowClick={onRowClick}
      loading={isLoading}
      skeletonRows={6}
      minWidth={980}
      empty={
        <ColocaEmpty
          icon={hasActiveFilters ? Inbox : tab === "pending" ? CheckCircle2 : Inbox}
          title={hasActiveFilters ? t("empty_filtered_description") : t(emptyTitleKey)}
          description={hasActiveFilters ? undefined : t(emptyDescriptionKey)}
          surface="card"
        >
          {hasActiveFilters && (
            <ColocaButton variant="default" onClick={onClearFilters}>
              {t("filter_clear")}
            </ColocaButton>
          )}
        </ColocaEmpty>
      }
      footer={paginationFooter}
    />
  );
}
